/**
 * People, agent and service names for ids (ADR-0054). The identity service resolves ids of the
 * caller's own tenant (`GET /v1/directory?ids=…`); unknown and other-tenant ids are omitted.
 * The resolver batches lookups from every component rendered in the same tick into one request
 * and caches answers (including "not found") for a few minutes.
 */
import type { Http } from "./http";

export type ActorKind = "user" | "agent" | "service";

export interface DirectoryEntry {
  display_name: string;
  kind: ActorKind;
  /** Client id of an agent or service (for the localised service name); null for people. */
  client_id?: string | null;
}

export type DirectoryOut = Record<string, DirectoryEntry>;

/** The service accepts at most this many ids per request. */
export const DIRECTORY_MAX_IDS = 200;

export function directoryApi(http: Http) {
  return {
    lookup: (ids: string[]) => http.get<DirectoryOut>("identity", "/v1/directory", { ids: ids.join(",") }),
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOOKUP = /^[A-Za-z0-9][A-Za-z0-9._:@-]{0,255}$/;

export const isUuid = (s: string): boolean => UUID.test(s);

/** Whether a string can be an id the directory resolves (not empty, no spaces, sane length). */
export const isLookupId = (s: string): boolean => LOOKUP.test(s);

/**
 * Friendly name of an agent from its client id or catalogue ref when the directory has none:
 * `agent-acme-approval-demo` → "Approval demo", `invoice-assistant@1.2.0` → "Invoice assistant".
 */
export function humaniseAgent(ref: string, tenant?: string | null): string {
  let s = ref.split("@")[0] ?? ref;
  if (tenant && s.startsWith(`agent-${tenant}-`)) s = s.slice(`agent-${tenant}-`.length);
  else if (s.startsWith("agent-")) {
    const rest = s.slice("agent-".length);
    // agent-<tenant>-<name>: drop the tenant segment when present.
    const dash = rest.indexOf("-");
    s = dash > 0 ? rest.slice(dash + 1) : rest;
  }
  const words = s.replace(/[-_.]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : ref;
}

export type Lookup = (ids: string[]) => Promise<DirectoryOut>;

export interface ResolverOptions {
  /** Delay to collect ids from components rendered together (ms). */
  batchMs?: number;
  /** How long an answer (or a miss) stays cached (ms). */
  ttlMs?: number;
  now?: () => number;
}

/** `undefined`: not looked up yet or pending; `null`: the directory does not know the id. */
export type Resolved = DirectoryEntry | null | undefined;

/** Batching, caching id → name resolver shared by every ActorName on the page. */
export class DirectoryResolver {
  private readonly cache = new Map<string, { entry: DirectoryEntry | null; at: number }>();
  private readonly queued = new Set<string>();
  private readonly inflight = new Set<string>();
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private version = 0;
  private readonly batchMs: number;
  private readonly ttlMs: number;
  private readonly now: () => number;

  constructor(
    private readonly lookup: Lookup,
    opts: ResolverOptions = {},
  ) {
    this.batchMs = opts.batchMs ?? 15;
    this.ttlMs = opts.ttlMs ?? 5 * 60_000;
    this.now = opts.now ?? Date.now;
  }

  /** Cached answer; queues a lookup when missing or stale. */
  get(id: string): Resolved {
    if (!id || !isLookupId(id)) return null;
    const hit = this.cache.get(id);
    const fresh = hit && this.now() - hit.at < this.ttlMs;
    if (!fresh) this.request(id);
    return hit ? hit.entry : undefined;
  }

  /** Changes whenever new answers arrive (for useSyncExternalStore). */
  snapshot = (): number => this.version;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private request(id: string): void {
    if (this.inflight.has(id) || this.queued.has(id)) return;
    this.queued.add(id);
    if (this.timer === null) this.timer = setTimeout(() => void this.flush(), this.batchMs);
  }

  /** Sends queued ids (in chunks of DIRECTORY_MAX_IDS). Exposed for tests. */
  async flush(): Promise<void> {
    this.timer = null;
    const ids = [...this.queued];
    this.queued.clear();
    for (let i = 0; i < ids.length; i += DIRECTORY_MAX_IDS) {
      const chunk = ids.slice(i, i + DIRECTORY_MAX_IDS);
      chunk.forEach((id) => this.inflight.add(id));
      let answer: DirectoryOut | null;
      try {
        answer = await this.lookup(chunk);
      } catch {
        answer = null; // directory unavailable: show fallbacks, retry after the TTL
      }
      const at = this.now();
      for (const id of chunk) {
        this.inflight.delete(id);
        const entry = answer?.[id];
        this.cache.set(id, { entry: entry && typeof entry.display_name === "string" && entry.display_name ? entry : null, at });
      }
    }
    this.version += 1;
    this.listeners.forEach((fn) => fn());
  }

  /** Forget everything (sign-out, user switch). */
  clear(): void {
    this.cache.clear();
    this.version += 1;
    this.listeners.forEach((fn) => fn());
  }
}
